import { useRestApiProvider } from "@jugl-web/rest-api";
import { useToast } from "@jugl-web/utils";
import {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { loadStripe } from "@stripe/stripe-js";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { PageLoaderFull } from "@jugl-web/ui-components";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useIdle } from "react-use";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import {
  UpdateCardAlert,
  UpdateCardAlertHandle,
} from "./components/UpdateCardAlert";
import { useEntityProvider } from "../EntityProvider/useEntityProvider";
import { SubscriptionUpdatedAlert } from "./components/SubscriptionUpdatedAlert";
import {
  EntitySubscriptionContext,
  EntitySubscriptionContextValue,
} from "./EntitySubscriptionContext";

const SHOULD_SHOW_SUBSCRIPTION_UPDATED_ALERT_KEY = `jugl::shouldShowSubscriptionUpdatedAlert`;

export const EntitySubscriptionProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const { navigateToPage } = useNavigation();
  const { entity, subscriptionInfo, refetchSubscriptionInfo } =
    useEntityProvider();
  const entityId = entity?.id;
  const { entitiesApi } = useRestApiProvider();
  const { isAnonymous } = useAnonymousSession();
  const { data: plans } = entitiesApi.useGetSubscriptionPlansQuery(
    entityId && !isAnonymous ? { entityId } : skipToken
  );
  const isIdle = useIdle(1);
  const [isPaymentInOtherWindow, setIsPaymentInOtherWindow] =
    useState<boolean>(false);

  useEffect(() => {
    if (isPaymentInOtherWindow && !isIdle) {
      window.open(`/${entityId}/workspace/subscription`, "_self");
    }
  }, [isIdle, isPaymentInOtherWindow, entityId]);

  // NOTE: as per backend request, we're not supporting
  // card payment for INR currency
  const isCardPaymentNotSupported = plans
    ? plans?.some((item) => item.currency === "inr")
    : false;

  const [isSubscriptionAlertOpen, setIsSubscriptionUpdatedPlan] =
    useState<boolean>(
      !!sessionStorage.getItem(SHOULD_SHOW_SUBSCRIPTION_UPDATED_ALERT_KEY)
    );
  useEffect(() => {
    sessionStorage.removeItem(SHOULD_SHOW_SUBSCRIPTION_UPDATED_ALERT_KEY);
  }, []);

  const { toast } = useToast({ variant: "web" });

  const updateCardAlertRef = useRef<UpdateCardAlertHandle>(null);

  const navigateToManageSubscription = useCallback(() => {
    navigateToPage("workspaceSelectSubscription");
  }, [navigateToPage]);

  const updateCardDetails = useCallback(async () => {
    if (!updateCardAlertRef.current) {
      throw new Error("Update card alert not rendered");
    }
    const result = await updateCardAlertRef.current.start();
    if (result.result === "success") {
      refetchSubscriptionInfo();
    }
    return result;
  }, [refetchSubscriptionInfo]);

  const [upgradeSubscription] = entitiesApi.useUpgradeSubscriptionMutation();
  const [subscriptionUpgradeInProgress, setSubscriptionUpgradeInProgress] =
    useState<boolean>(false);

  const applyAppSumoSubscription: EntitySubscriptionContextValue["applyAppSumoSubscription"] =
    useCallback(
      async (planId, promoCodes) => {
        if (!entityId) {
          return;
        }
        setSubscriptionUpgradeInProgress(true);
        const response = await upgradeSubscription({
          entityId,
          data: {
            price_id: planId,
            promo_code: promoCodes,
          },
        });
        const handleError = () => {
          setSubscriptionUpgradeInProgress(false);
          toast("Failed to upgrade subscription", { variant: "error" });
        };
        if ("error" in response && response.error) {
          handleError();
          return;
        }
        await new Promise((r) => {
          setTimeout(r, 3000);
        });
        sessionStorage.setItem(SHOULD_SHOW_SUBSCRIPTION_UPDATED_ALERT_KEY, "1");
        window.open(`/${entityId}/workspace/subscription`, "_self");
      },
      [entityId, toast, upgradeSubscription]
    );

  const changeSubscription: EntitySubscriptionContextValue["changeSubscription"] =
    useCallback(
      async (plan, seatsCount, promoCode) => {
        if (!entityId) {
          return;
        }
        if (
          !isCardPaymentNotSupported &&
          plan.enforcePaymentMethod &&
          !subscriptionInfo?.card
        ) {
          const result = await updateCardDetails();
          if (result.result !== "success") {
            return;
          }
          setSubscriptionUpgradeInProgress(true);
          await new Promise((r) => {
            setTimeout(r, 2000);
          });
        } else {
          setSubscriptionUpgradeInProgress(true);
        }
        const response = await upgradeSubscription({
          entityId,
          data: {
            price_id: plan.id,
            promo_code: promoCode,
            additional_seats:
              seatsCount || plan.additionalSeatPriceId
                ? {
                    qty: seatsCount || 0,
                    id: plan.additionalSeatPriceId,
                  }
                : undefined,
          },
        });
        const handleError = () => {
          setSubscriptionUpgradeInProgress(false);
          toast("Failed to upgrade subscription", { variant: "error" });
        };
        if ("error" in response && response.error) {
          handleError();
          return;
        }
        if (
          "data" in response &&
          "result" in response.data &&
          response.data.result === "action_required" &&
          entityId
        ) {
          const stripe = await loadStripe(response.data.pub_key);
          if (!stripe) {
            handleError();
            return;
          }
          const nextActionResult = await stripe?.handleNextAction({
            clientSecret: response.data.secret,
          });
          if (
            nextActionResult?.error ||
            nextActionResult?.paymentIntent?.status !== "succeeded"
          ) {
            handleError();
            return;
          }
        }
        if ("data" in response && "payment_link" in response.data) {
          window.open(response.data.payment_link as string, "_blank");
          sessionStorage.setItem(
            SHOULD_SHOW_SUBSCRIPTION_UPDATED_ALERT_KEY,
            "1"
          );
          setTimeout(() => {
            setIsPaymentInOtherWindow(true);
          }, 1000);
          return;
        }
        await new Promise((r) => {
          setTimeout(r, 3000);
        });
        sessionStorage.setItem(SHOULD_SHOW_SUBSCRIPTION_UPDATED_ALERT_KEY, "1");
        window.open(`/${entityId}/workspace/subscription`, "_self");
      },
      [
        entityId,
        updateCardDetails,
        upgradeSubscription,
        subscriptionInfo,
        toast,
        isCardPaymentNotSupported,
      ]
    );

  const [retryInvoice, { isLoading: isRetryInvoiceLoading }] =
    entitiesApi.useRetryInvoiceMutation();
  const [isRetryInvoiceProcessing, setIsRetryInvoiceProcessing] =
    useState(false);
  const retryLastInvoicePayment = useCallback(async () => {
    if (!entityId) {
      throw new Error("Entity ID is not available");
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const response: any = await retryInvoice({
      entityId,
    });
    if (!response.data) {
      throw new Error("Failed to retry invoice");
    }
    if (response.data.result === "success") {
      sessionStorage.setItem(SHOULD_SHOW_SUBSCRIPTION_UPDATED_ALERT_KEY, "1");
      window.location.reload();
      return;
    }
    if ("data" in response && "payment_link" in response.data) {
      window.open(response.data.payment_link as string, "_blank");
      sessionStorage.setItem(SHOULD_SHOW_SUBSCRIPTION_UPDATED_ALERT_KEY, "1");
      setTimeout(() => {
        setIsPaymentInOtherWindow(true);
      }, 1000);
      return;
    }
    if (response.data.result !== "action_required") {
      return;
    }
    const stripe = await loadStripe(response.data.pub_key);
    if (!stripe) {
      throw new Error("Failed to load stripe");
    }
    const nextActionResult = await stripe?.handleNextAction({
      clientSecret: response.data.secret,
    });
    if (
      nextActionResult?.error ||
      nextActionResult?.paymentIntent?.status !== "succeeded"
    ) {
      throw new Error(nextActionResult?.error?.message || "Payment failed");
    }
    await retryLastInvoicePayment();
    setIsRetryInvoiceProcessing(false);
  }, [entityId, retryInvoice]);

  const value: EntitySubscriptionContextValue = useMemo(
    () => ({
      navigateToManageSubscription,
      updateCardDetails,
      changeSubscription,
      applyAppSumoSubscription,
      retryLastInvoicePayment,
      isRetryInvoiceLoading: isRetryInvoiceProcessing || isRetryInvoiceLoading,
      isCardPaymentNotSupported,
    }),
    [
      navigateToManageSubscription,
      updateCardDetails,
      changeSubscription,
      applyAppSumoSubscription,
      retryLastInvoicePayment,
      isRetryInvoiceLoading,
      isRetryInvoiceProcessing,
      isCardPaymentNotSupported,
    ]
  );

  return (
    <EntitySubscriptionContext.Provider value={value}>
      {children}
      <PageLoaderFull
        isActive={subscriptionUpgradeInProgress || isPaymentInOtherWindow}
        isTransparent
      />
      {entityId && (
        <UpdateCardAlert ref={updateCardAlertRef} entityId={entityId} />
      )}
      {subscriptionInfo &&
        (entity?.role === "admin" ||
          (subscriptionInfo.status !== "incomplete" &&
            subscriptionInfo.status !== "past_due")) && (
          <SubscriptionUpdatedAlert
            isOpen={isSubscriptionAlertOpen}
            subscriptionInfo={subscriptionInfo}
            onRequestClose={() => setIsSubscriptionUpdatedPlan(false)}
            onRequestRetry={retryLastInvoicePayment}
            isRetryLoading={isRetryInvoiceLoading}
          />
        )}
    </EntitySubscriptionContext.Provider>
  );
};
